import { formatNameWithCode, hasText } from '~/utils';
import { formatCityRegionZip, type AddressLike } from '~/utils/addressFormat';

type AddressLabelFacility = {
  address: Pick<
    AddressLike,
    'addressLine1' | 'addressLine2' | 'city' | 'region'
  >;
};

export type ShipFromFacility = AddressLabelFacility & {
  id: string;
  code?: string | null;
  locations: { code: string }[];
};

export type FacilityChoice = { id: string; label: string };

export function facilityAddressLabel(facility: AddressLabelFacility): string {
  const { addressLine1, addressLine2, city, region } = facility.address;
  const street = [addressLine1, addressLine2].filter(hasText).join(', ');
  return [formatCityRegionZip({ city, region }), street]
    .filter(hasText)
    .join(' · ');
}

function distinguishingCode(facility: ShipFromFacility): string {
  if (hasText(facility.code)) return facility.code.trim();
  const locationCodes = facility.locations
    .map((location) => location.code.trim())
    .filter(hasText)
    .join(', ');
  return locationCodes || facility.id.slice(0, 13);
}

export function shipFromFacilityChoices(
  facilities: ShipFromFacility[]
): FacilityChoice[] {
  const labelled = facilities.map((facility) => ({
    facility,
    label: facilityAddressLabel(facility),
  }));
  const facilityCountByLabel = new Map<string, number>();
  for (const { label } of labelled) {
    facilityCountByLabel.set(label, (facilityCountByLabel.get(label) ?? 0) + 1);
  }
  return labelled
    .map(({ facility, label }) => {
      const code = distinguishingCode(facility);
      const sharesAddress = (facilityCountByLabel.get(label) ?? 0) > 1;
      return {
        id: facility.id,
        label: sharesAddress ? formatNameWithCode(label, code) : label,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function shipFromSelectOptions(
  facilities: FacilityChoice[],
  placeholder: string
): { label: string; value: string }[] {
  const facilityOptions = facilities.map((facility) => ({
    label: facility.label,
    value: facility.id,
  }));
  return facilities.length === 1
    ? facilityOptions
    : [{ label: placeholder, value: '' }, ...facilityOptions];
}

export function shipFromFacilityLabel(
  facility: AddressLabelFacility & { id: string },
  facilities: ShipFromFacility[]
): string {
  const choice = shipFromFacilityChoices(facilities).find(
    (candidate) => candidate.id === facility.id
  );
  return choice ? choice.label : facilityAddressLabel(facility);
}

export function facilityAddressCodeLabel(
  facility: AddressLabelFacility & { code?: string | null }
): string {
  return formatNameWithCode(facilityAddressLabel(facility), facility.code);
}
